import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { ROOT } from './requests';
import { AuthExpiredError, getValidAccessToken, msUntilRefreshDue } from './tokenRefreshService';
import { clearTokensAndRedirectToLogin } from './fetchWithAuth';

// jsonp-polling (and the iframe transports) can't pass the server's origin check.
const SOCKJS_TRANSPORTS = ['websocket', 'xhr-streaming', 'xhr-polling'];
const RECONNECT_DELAY_MS = 5000;
const CONNECTION_TIMEOUT_MS = 20000;
const MIN_TOKEN_CHECK_DELAY_MS = 30 * 1000;

/**
 * STOMP-over-SockJS connection that handshakes with a fresh access token on every
 * (re)connect, keeps the session's tokens refreshed while the page stays open, and
 * reconnects right away when the tab becomes visible again.
 *
 * `onConnect(client)` runs after every successful (re)connect, so subscriptions
 * belong there. `getConnectHeaders()` is read before every attempt.
 */
export function createStompConnection({ getConnectHeaders, onConnect, onConnectionChange }) {
    let accessToken = null;
    let tokenTimer = null;
    let stopped = false;

    const client = new Client({
        reconnectDelay: RECONNECT_DELAY_MS,
        connectionTimeout: CONNECTION_TIMEOUT_MS,
        // Server sends heartbeats; client ones are off because background tabs throttle timers.
        heartbeatIncoming: 10000,
        heartbeatOutgoing: 0,
        debug: () => {},
        webSocketFactory: () => new SockJS(
            `${ROOT}ws?token=${encodeURIComponent(accessToken ?? '')}`,
            null,
            { transports: SOCKJS_TRANSPORTS }
        ),
        // Must never throw: a rejected beforeConnect silently ends the reconnect loop.
        beforeConnect: async () => {
            client.connectHeaders = getConnectHeaders ? getConnectHeaders() : {};
            await ensureFreshToken();
        },
        onConnect: () => {
            onConnectionChange?.(true);
            onConnect(client);
        },
        onWebSocketClose: () => {
            if (!stopped) onConnectionChange?.(false);
        },
    });

    async function ensureFreshToken() {
        try {
            accessToken = await getValidAccessToken();
            return true;
        } catch (e) {
            if (e instanceof AuthExpiredError) {
                stop();
                clearTokensAndRedirectToLogin();
                return false;
            }
            // API unreachable: try with what we have; a failed attempt is retried after reconnectDelay.
            accessToken = localStorage.getItem('access_token');
            return false;
        }
    }

    // Live pages can go hours without a REST call; refreshing here keeps the refresh token from lapsing.
    function scheduleTokenRefresh(delay) {
        clearTimeout(tokenTimer);
        tokenTimer = setTimeout(async () => {
            const ok = await ensureFreshToken();
            if (!stopped) scheduleTokenRefresh(ok ? msUntilRefreshDue() : MIN_TOKEN_CHECK_DELAY_MS);
        }, Math.max(delay, MIN_TOKEN_CHECK_DELAY_MS));
    }

    function onVisibilityChange() {
        if (stopped || document.visibilityState !== 'visible') return;
        // Waiting for the reconnect timer (no socket yet): skip the wait.
        if (client.active && !client.connected && !client.webSocket) {
            client.deactivate().then(() => {
                if (!stopped) client.activate();
            });
        }
    }

    function start() {
        if (stopped) return;
        document.addEventListener('visibilitychange', onVisibilityChange);
        scheduleTokenRefresh(msUntilRefreshDue());
        client.activate();
    }

    function stop() {
        if (stopped) return;
        stopped = true;
        clearTimeout(tokenTimer);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        client.deactivate();
    }

    return { client, start, stop };
}
